import LocationSearchIcon from "@/assets/icons/LocationSearch.module.svg";
import { usePinnedGridHighlight } from "@/grid/usePinnedGridHighlight";
import { useExportStatus } from "@/hooks/useExportStatus";
import { OffsetControl } from "@/overlay/components/QuickAccessLabel/ControlComponents/OffsetControl";
import { LabelDescription } from "@/overlay/components/QuickAccessLabel/LabelDescription";
import { LabelTitle } from "@/overlay/components/QuickAccessLabel/LabelTitle";
import { OffsetDescription } from "@/overlay/components/QuickAccessLabel/OffsetDescription";
import styles from "@/overlay/components/QuickAccessLabel/QuickAccessLabel.module.scss";
import { useLabelPopovers } from "@/overlay/components/QuickAccessLabel/hooks/useLabelPopovers";
import { useLocationSearchWindow } from "@/overlay/components/QuickAccessLabel/hooks/useLocationSearchWindow";
import type { BaseLabelProps } from "@/overlay/components/QuickAccessLabel/labelTypes";
import { LabelChip, LabelIcon } from "@/overlay/components/QuickAccessLabel/parts/LabelChip";
import { LabelPopover, LabelStylePopover } from "@/overlay/components/QuickAccessLabel/parts/LabelPopover";
import { LabelShell } from "@/overlay/components/QuickAccessLabel/parts/LabelShell";
import { convertToCSSProperties } from "@/utility/style";
import { t } from "@lingui/core/macro";
import type { JSONInitDate } from "@mm/api.meteomatics.com";
import type { LocationResult } from "@mm/geocoder.meteomatics.com/models";
import { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { MmTooltipWrapper } from "@mm/zeus-react";
import { DateTime } from "luxon";
import { useRef } from "react";

/** Class for popovers below the map label, positioned by the map overlay (see overlays/default/style.scss). */
const MAP_POPOVER_CLASS = "map-label-popover";

export type MapLabelProps = BaseLabelProps & {
  displayTime?: DateTime;
  timeOffset?: number;
  allInitDates?: JSONInitDate[];
  onOffsetChange: (newOffset: number) => void;
  updateMapViewportToPoint: (newLocation: LocationResult) => void;
};

export function MapLabel({
  title,
  titleStyle,
  timeOffset,
  allInitDates,
  displayTime,
  timezone,
  isLoading,
  gridHighlightType,
  gridHighlightId,
  onTitleStyleChange,
  onTitleChange,
  onOffsetChange,
  disabled,
  updateMapViewportToPoint,
}: MapLabelProps) {
  const { isExportActive } = useExportStatus();
  const componentRef = useRef<HTMLDivElement>(null);
  const popovers = useLabelPopovers<"offset">(componentRef);

  const locationWindow = useLocationSearchWindow({
    sourceCellName: title || undefined,
    showStationSearch: false,
    onSelect: (newLocation) => updateMapViewportToPoint(newLocation),
  });

  usePinnedGridHighlight({
    active: locationWindow.isOpen,
    type: gridHighlightType,
    id: gridHighlightId,
  });

  if (!displayTime) {
    return <></>;
  }

  const handleLocationClick = () => {
    if (popovers.requestClose()) {
      locationWindow.open();
    }
  };

  return (
    <LabelShell
      ref={componentRef}
      variant="map"
      dataTestId="map-label"
      loadingTestId="map-label-loading"
      isLoading={isLoading}
      popover={
        <>
          {popovers.isOpen("style") && (
            <LabelStylePopover
              className={MAP_POPOVER_CLASS}
              titleStyle={titleStyle}
              onStyleChange={onTitleStyleChange}
              registerCloseHandler={popovers.registerCloseHandler}
              popoverRef={popovers.popoverRef}
            />
          )}
          {popovers.isOpen("offset") && (
            <LabelPopover className={MAP_POPOVER_CLASS} ref={popovers.popoverRef}>
              <OffsetControl offsetMinutes={timeOffset || 0} onOffsetChange={onOffsetChange} />
            </LabelPopover>
          )}
        </>
      }
      footer={
        allInitDates &&
        allInitDates.length > 0 && (
          <div
            className={styles["model-runs"]}
            style={convertToCSSProperties({ ...titleStyle, fontSize: (titleStyle?.fontSize || 15) - 3 })}
          >
            <div className={styles["model-runs-header"]}>
              <div>Parameter</div>
              <div className={styles.divider} />
              <div>Model</div>
              <div className={styles.divider} />
              <div>Run</div>
            </div>
            {allInitDates.map((item) => (
              <div className={styles["model-run"]} key={`${item?.model}-${item?.parameter}`}>
                <div>{item?.parameter}</div>
                <div className={styles.divider} />
                <div className="model-name">{item?.model}</div>
                <div className={styles.divider} />
                <div>
                  <LabelDescription
                    timezone={GuiTimeZone.utc}
                    datetimeStart={DateTime.fromISO(item?.initDate.toString())}
                  />
                </div>
              </div>
            ))}
          </div>
        )
      }
    >
      <LabelTitle
        title={title || "New Map"}
        onTitleChange={onTitleChange}
        onTitleClick={() => popovers.toggle("style")}
        titleStyle={titleStyle}
        disabled={disabled}
      />
      {!isExportActive && (
        <MmTooltipWrapper tooltipText={t`Location search`} position="bottom" tail={true}>
          {/* Moving the viewport is not a tool edit, so the button stays usable while the label is disabled. */}
          <LabelChip variant="icon" active={locationWindow.isOpen} onClick={handleLocationClick}>
            <div className={styles["label-icon-container"]} style={{ fontSize: titleStyle?.fontSize || "1rem" }}>
              <LocationSearchIcon />
            </div>
          </LabelChip>
        </MmTooltipWrapper>
      )}
      <MmTooltipWrapper
        tooltipText={t`Time Offset`}
        position="bottom"
        tail={true}
        disabled={popovers.active !== null || disabled}
      >
        <LabelChip disabled={disabled} onClick={() => popovers.toggle("offset")}>
          <OffsetDescription offsetMinutes={timeOffset} titleStyle={titleStyle} disabled={disabled} />
          <LabelIcon name="date_range" titleStyle={titleStyle} />
          <LabelDescription
            timezone={timezone}
            datetimeStart={displayTime}
            titleStyle={titleStyle}
            disabled={disabled}
          />
        </LabelChip>
      </MmTooltipWrapper>
    </LabelShell>
  );
}
